import React, { useMemo, useState } from 'react';
import { Tag, AlertTriangle, CheckCircle, Search, Layers, AlertOctagon, ChevronDown, ChevronRight, Filter, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Question, ModuleId } from '../../types';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { MODULES } from '../../data/modules';
import { getLevelById } from '../../config/levels';
interface FamilyIdExplorerProps {
  questions: Question[];
}
type FamilyGroup = {
  id: string;
  module: ModuleId;
  questions: Question[];
  levels: Set<number>;
  skills: Set<string>;
  subSkills: Set<string>;
  isCrossLevel: boolean;
  isMigrated: boolean; // Heuristic: > 1 question
};
type ModuleStats = {
  moduleId: ModuleId;
  totalQuestions: number;
  totalFamilies: number;
  questionsWithFamily: number;
  avgQuestionsPerFamily: number;
  crossLevelCount: number;
  migrationStatus: 'complete' | 'partial' | 'pending';
};
export function FamilyIdExplorer({
  questions
}: FamilyIdExplorerProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedModule, setSelectedModule] = useState<ModuleId | 'all'>('all');
  const [expandedFamilies, setExpandedFamilies] = useState<Set<string>>(new Set());
  const [filterType, setFilterType] = useState<'all' | 'cross-level' | 'single' | 'missing'>('all');
  // Process Data
  const {
    familyGroups,
    moduleStats,
    globalStats,
    missingFamilyQuestions
  } = useMemo(() => {
    const groups = new Map<string, FamilyGroup>();
    const missing: Question[] = [];
    const modStats = new Map<ModuleId, {
      totalQuestions: number;
      questionsWithFamily: number;
      families: Set<string>;
      crossLevelFamilies: number;
    }>();
    // Initialize module stats
    MODULES.forEach(m => {
      modStats.set(m.id, {
        totalQuestions: 0,
        questionsWithFamily: 0,
        families: new Set(),
        crossLevelFamilies: 0
      });
    });
    questions.forEach(q => {
      // Update basic module counts
      const mStat = modStats.get(q.module);
      if (mStat) {
        mStat.totalQuestions++;
      }
      if (!q.familyId) {
        missing.push(q);
        return;
      }
      if (mStat) {
        mStat.questionsWithFamily++;
        mStat.families.add(q.familyId);
      }
      if (!groups.has(q.familyId)) {
        groups.set(q.familyId, {
          id: q.familyId,
          module: q.module,
          questions: [],
          levels: new Set(),
          skills: new Set(),
          subSkills: new Set(),
          isCrossLevel: false,
          isMigrated: false
        });
      }
      const group = groups.get(q.familyId)!;
      group.questions.push(q);
      group.levels.add(q.difficulty);
      if (q.skill) group.skills.add(q.skill);
      if (q.subSkill) group.subSkills.add(q.subSkill);
    });
    // Finalize groups
    const processedGroups = Array.from(groups.values()).map(g => {
      g.isCrossLevel = g.levels.size > 1;
      g.isMigrated = g.questions.length > 1; // Simple heuristic
      // Update module cross-level count
      const mStat = modStats.get(g.module);
      if (mStat && g.isCrossLevel) {
        mStat.crossLevelFamilies++;
      }
      return g;
    });
    // Finalize module stats
    const finalModuleStats: ModuleStats[] = MODULES.map(m => {
      const s = modStats.get(m.id)!;
      const totalFamilies = s.families.size;
      const avg = totalFamilies > 0 ? s.questionsWithFamily / totalFamilies : 0;
      let status: ModuleStats['migrationStatus'] = 'pending';
      if (avg > 1.5 && s.questionsWithFamily / Math.max(1, s.totalQuestions) > 0.8) {
        status = 'complete';
      } else if (avg > 1.1 || s.questionsWithFamily > 0) {
        status = 'partial';
      }
      return {
        moduleId: m.id,
        totalQuestions: s.totalQuestions,
        totalFamilies: totalFamilies,
        questionsWithFamily: s.questionsWithFamily,
        avgQuestionsPerFamily: avg,
        crossLevelCount: s.crossLevelFamilies,
        migrationStatus: status
      };
    });
    return {
      familyGroups: processedGroups,
      moduleStats: finalModuleStats,
      missingFamilyQuestions: missing,
      globalStats: {
        totalFamilies: processedGroups.length,
        crossLevelFamilies: processedGroups.filter(g => g.isCrossLevel).length,
        singleQuestionFamilies: processedGroups.filter(g => g.questions.length === 1).length,
        missingCount: missing.length
      }
    };
  }, [questions]);
  // Filtering
  const filteredFamilies = useMemo(() => {
    return familyGroups.filter(g => {
      const matchesSearch = g.id.toLowerCase().includes(searchTerm.toLowerCase()) || Array.from(g.subSkills).some(s => s.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesModule = selectedModule === 'all' || g.module === selectedModule;
      let matchesType = true;
      if (filterType === 'cross-level') matchesType = g.isCrossLevel;
      if (filterType === 'single') matchesType = g.questions.length === 1;
      return matchesSearch && matchesModule && matchesType;
    }).sort((a, b) => {
      // Sort by cross-level first, then by question count (desc)
      if (a.isCrossLevel !== b.isCrossLevel) return a.isCrossLevel ? -1 : 1;
      return b.questions.length - a.questions.length;
    });
  }, [familyGroups, searchTerm, selectedModule, filterType]);
  const toggleFamily = (id: string) => {
    const newSet = new Set(expandedFamilies);
    if (newSet.has(id)) newSet.delete(id);else newSet.add(id);
    setExpandedFamilies(newSet);
  };
  return <div className="space-y-6" data-id="element-634">
      {/* 1. Top Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-635">
        <StatCard label="Total Families" value={globalStats.totalFamilies} icon={<Tag className="w-4 h-4 text-indigo-500" data-id="element-637" />} data-id="element-636" />
        <StatCard label="Cross-Level" value={globalStats.crossLevelFamilies} icon={<Layers className="w-4 h-4 text-emerald-500" data-id="element-639" />} subtext={`${Math.round(globalStats.crossLevelFamilies / Math.max(1, globalStats.totalFamilies) * 100)}% of total`} data-id="element-638" />
        <StatCard label="Single Question" value={globalStats.singleQuestionFamilies} icon={<AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-641" />} isWarning={globalStats.singleQuestionFamilies > 0} data-id="element-640" />
        <StatCard label="Missing FamilyId" value={globalStats.missingCount} icon={<AlertOctagon className="w-4 h-4 text-rose-500" data-id="element-643" />} isError={globalStats.missingCount > 0} data-id="element-642" />
      </div>

      {/* 2. Module Migration Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-644">
        {moduleStats.map(stat => {
        const module = MODULES.find(m => m.id === stat.moduleId);
        if (!module) return null;
        return <Card key={stat.moduleId} className="p-4 flex flex-col h-full" data-id="element-645">
              <div className="flex items-center justify-between mb-3" data-id="element-646">
                <div className="flex items-center gap-2" data-id="element-647">
                  <div className={`w-2 h-2 rounded-full ${module.color.replace('text-', 'bg-')}`} data-id="element-648" />
                  <span className="font-bold text-sm text-slate-700" data-id="element-649">
                    {module.title}
                  </span>
                </div>
                {stat.migrationStatus === 'complete' && <Badge variant="success" size="sm" icon={<CheckCircle className="w-3 h-3" data-id="element-651" />} data-id="element-650">
                    Gemigreerd
                  </Badge>}
                {stat.migrationStatus === 'partial' && <Badge variant="warning" size="sm" data-id="element-652">
                    Deels
                  </Badge>}
                {stat.migrationStatus === 'pending' && <Badge variant="neutral" size="sm" data-id="element-653">
                    Nog niet
                  </Badge>}
              </div>

              <div className="space-y-2 mt-auto" data-id="element-654">
                <div className="flex justify-between text-xs" data-id="element-655">
                  <span className="text-slate-500" data-id="element-656">Families</span>
                  <span className="font-mono font-bold text-slate-700" data-id="element-657">
                    {stat.totalFamilies}
                  </span>
                </div>
                <div className="flex justify-between text-xs" data-id="element-658">
                  <span className="text-slate-500" data-id="element-659">Avg Q/Family</span>
                  <span className={`font-mono font-bold ${stat.avgQuestionsPerFamily > 1.5 ? 'text-emerald-600' : 'text-amber-600'}`} data-id="element-660">
                    {stat.avgQuestionsPerFamily.toFixed(1)}
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden mt-2" data-id="element-661">
                  <div className={`h-full rounded-full ${stat.migrationStatus === 'complete' ? 'bg-emerald-500' : 'bg-indigo-500'}`} style={{
                width: `${stat.questionsWithFamily / Math.max(1, stat.totalQuestions) * 100}%`
              }} data-id="element-662" />
                </div>
              </div>
            </Card>;
      })}
      </div>

      {/* 3. Main Explorer */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col h-[600px]" data-id="element-663">
        {/* Toolbar */}
        <div className="p-4 border-b border-slate-200 flex flex-wrap gap-3 bg-slate-50" data-id="element-664">
          <div className="relative flex-1 min-w-[200px]" data-id="element-665">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-666" />
            <input type="text" placeholder="Zoek op ID of concept..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-667" />
          </div>

          <select value={selectedModule} onChange={e => setSelectedModule(e.target.value as ModuleId | 'all')} className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-668">
            <option value="all" data-id="element-669">Alle Vakken</option>
            {MODULES.map(m => <option key={m.id} value={m.id} data-id="element-670">
                {m.title}
              </option>)}
          </select>

          <div className="flex bg-white rounded-lg border border-slate-200 p-1" data-id="element-671">
            <FilterButton active={filterType === 'all'} onClick={() => setFilterType('all')} label="Alles" data-id="element-672" />
            <FilterButton active={filterType === 'cross-level'} onClick={() => setFilterType('cross-level')} label="Cross-Level" icon={<Layers className="w-3 h-3" data-id="element-674" />} data-id="element-673" />
            <FilterButton active={filterType === 'single'} onClick={() => setFilterType('single')} label="Single" icon={<AlertTriangle className="w-3 h-3" data-id="element-676" />} data-id="element-675" />
            <FilterButton active={filterType === 'missing'} onClick={() => setFilterType('missing')} label="Missing" icon={<AlertOctagon className="w-3 h-3" data-id="element-678" />} data-id="element-677" />
          </div>
        </div>

        {/* List Content */}
        <div className="flex-1 overflow-y-auto p-0" data-id="element-679">
          {filterType === 'missing' ? <div className="p-4 space-y-2" data-id="element-680">
              <div className="text-sm font-bold text-rose-700 mb-2 flex items-center gap-2" data-id="element-681">
                <AlertOctagon className="w-4 h-4" data-id="element-682" />
                {missingFamilyQuestions.length} Vragen zonder FamilyId
              </div>
              {missingFamilyQuestions.map(q => <div key={q.id} className="flex items-center justify-between p-3 bg-rose-50 border border-rose-100 rounded-lg" data-id="element-683">
                  <div data-id="element-684">
                    <div className="font-mono text-xs font-bold text-rose-800" data-id="element-685">
                      {q.id}
                    </div>
                    <div className="text-sm text-rose-900 line-clamp-1" data-id="element-686">
                      {q.question}
                    </div>
                  </div>
                  <Badge variant="danger" size="sm" data-id="element-687">
                    {q.module}
                  </Badge>
                </div>)}
              {missingFamilyQuestions.length === 0 && <div className="text-center py-12 text-emerald-600 flex flex-col items-center" data-id="element-688">
                  <CheckCircle className="w-8 h-8 mb-2" data-id="element-689" />
                  <p data-id="element-690">Geen vragen zonder FamilyId!</p>
                </div>}
            </div> : <div className="divide-y divide-slate-100" data-id="element-691">
              {filteredFamilies.length === 0 ? <div className="text-center py-12 text-slate-400" data-id="element-692">
                  Geen families gevonden met deze filters
                </div> : filteredFamilies.map(group => {
            const isExpanded = expandedFamilies.has(group.id);
            const levels = Array.from(group.levels).sort((a, b) => a - b);
            const levelLabels = levels.map(l => getLevelById(l)?.label || l);
            return <div key={group.id} className="bg-white hover:bg-slate-50 transition-colors" data-id="element-693">
                      <div className="flex items-center p-3 cursor-pointer" onClick={() => toggleFamily(group.id)} data-id="element-694">
                        <div className="mr-3 text-slate-400" data-id="element-695">
                          {isExpanded ? <ChevronDown className="w-4 h-4" data-id="element-696" /> : <ChevronRight className="w-4 h-4" data-id="element-697" />}
                        </div>

                        <div className="flex-1 min-w-0" data-id="element-698">
                          <div className="flex items-center gap-2 mb-1" data-id="element-699">
                            <span className="font-mono font-bold text-sm text-slate-700" data-id="element-700">
                              {group.id}
                            </span>
                            {group.isCrossLevel && <Badge variant="success" size="sm" icon={<Layers className="w-3 h-3" data-id="element-702" />} data-id="element-701">
                                Cross-Level
                              </Badge>}
                            {!group.isMigrated && <Badge variant="warning" size="sm" data-id="element-703">
                                Single
                              </Badge>}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-500" data-id="element-704">
                            <span className="capitalize" data-id="element-705">
                              {Array.from(group.subSkills)[0] || 'Unknown SubSkill'}
                            </span>
                            <span data-id="element-706">•</span>
                            <span data-id="element-707">{group.questions.length} vragen</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2" data-id="element-708">
                          {levelLabels.map(l => <span key={l} className="px-1.5 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-bold rounded" data-id="element-709">
                              {l}
                            </span>)}
                        </div>
                      </div>

                      <AnimatePresence data-id="element-710">
                        {isExpanded && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="overflow-hidden bg-slate-50/50 border-t border-slate-100" data-id="element-711">
                            <div className="p-3 pl-10 space-y-2" data-id="element-712">
                              {group.questions.map(q => <div key={q.id} className="flex items-start gap-3 text-sm p-2 bg-white rounded border border-slate-200" data-id="element-713">
                                  <div className="font-mono text-xs text-slate-400 mt-0.5" data-id="element-714">
                                    {q.id}
                                  </div>
                                  <div className="flex-1" data-id="element-715">
                                    <div className="text-slate-800" data-id="element-716">
                                      {q.question}
                                    </div>
                                    <div className="text-xs text-slate-400 mt-1 flex gap-2" data-id="element-717">
                                      <span data-id="element-718">Lvl {q.difficulty}</span>
                                      <span data-id="element-719">•</span>
                                      <span data-id="element-720">{q.questionType}</span>
                                    </div>
                                  </div>
                                </div>)}
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </div>;
          })}
            </div>}
        </div>
      </div>
    </div>;
}
function StatCard({
  label,
  value,
  icon,
  subtext,
  isError = false,
  isWarning = false
}: any) {
  let borderColor = 'border-slate-200';
  let bgColor = 'bg-white';
  let textColor = 'text-slate-900';
  if (isError) {
    borderColor = 'border-rose-200';
    bgColor = 'bg-rose-50';
    textColor = 'text-rose-900';
  } else if (isWarning) {
    borderColor = 'border-amber-200';
    bgColor = 'bg-amber-50';
    textColor = 'text-amber-900';
  }
  return <div className={`p-4 rounded-xl border ${borderColor} ${bgColor}`} data-id="element-721">
      <div className="flex items-center justify-between mb-1" data-id="element-722">
        <span className={`text-xs font-bold uppercase tracking-wider ${isError ? 'text-rose-600' : isWarning ? 'text-amber-600' : 'text-slate-400'}`} data-id="element-723">
          {label}
        </span>
        {icon}
      </div>
      <div className={`text-2xl font-bold ${textColor}`} data-id="element-724">{value}</div>
      {subtext && <div className="text-[10px] text-slate-400 mt-1" data-id="element-725">{subtext}</div>}
    </div>;
}
function FilterButton({
  active,
  onClick,
  label,
  icon
}: any) {
  return <button onClick={onClick} className={`
        flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors
        ${active ? 'bg-indigo-100 text-indigo-700' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}
      `} data-id="element-726">
      {icon}
      {label}
    </button>;
}